import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Anchor,
  Avatar,
  Button,
  Checkbox,
  Divider,
  Image,
  Skeleton,
  Tabs,
  TextInput
} from '@mantine/core';
import { AlertCircle } from 'tabler-icons-react';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import CustomNavLink from '../../common/CustomNavLink';
import { getInitialsFromName } from '../../../helpers/format';
import SettingSelector from '../../common/SettingSelector';
import { triggerNotification } from '../../../helpers/notification';

const SelectStoreTeamModal = ({
  isOpen,
  onClose,
  onConfirm,
  onRemove,
  team,
  association,
  isAssociationSelect
}) => {
  const hasFetched = useRef(false);
  const { state, fetchStoreCreateOptions } = useContext(StoreContext);
  const [formState, setFormState] = useState({
    selectedTeam: null,
    selectedAssociation: null,
    overrideColors: false,
    search: '',
    activeTab: 'teams'
  });

  const { loading, value: createOptions } = state.ecomStoreCreateOptions || {};

  const regAssociationTeams =
    createOptions?.regAssociations.reduce((r, c) => {
      c.teams.forEach((t) => {
        if (!r.some((x) => x.pkTeam === t.pkTeam)) {
          r.push({
            ...t,
            pkRegAssociation: c.pkRegAssociation,
            associationName: c.name
          });
        }
      });
      return r;
    }, []) || [];
  const personalTeams = createOptions?.teams || [];
  const filteredTeams = (formState.activeTab === 'association'
    ? regAssociationTeams
    : personalTeams
  ).filter(
    (t) =>
      !formState.search ||
      t.name.toLowerCase().includes(formState.search.toLowerCase())
  );

  const associationOptions = createOptions
    ? [
        ...createOptions.regAssociations.flatMap((t) => t.teams),
        ...createOptions.teams
      ]
        .reduce(
          (r, c) =>
            r.find((a) => a.pkAssociation === c.fkAssociation)
              ? r
              : [
                  ...r,
                  {
                    pkAssociation: c.fkAssociation,
                    name: c.associationName,
                    value: c.fkAssociation.toString(),
                    image: c.associationImage
                  }
                ],
          []
        )
        .filter(
          (t) =>
            !formState.search ||
            t.name.toLowerCase().includes(formState.search.toLowerCase())
        )
    : [];

  const selectedRegAssociation = createOptions?.regAssociations.find((a) =>
    formState.selectedAssociation
      ? a.fkAssociation === formState.selectedAssociation.pkAssociation
      : formState.selectedTeam
      ? a.teams.some((t) => t.pkTeam === formState.selectedTeam.pkTeam)
      : null
  );

  useEffect(() => {
    if (isOpen) {
      if (!loading && hasFetched.current) {
        setFormState({
          selectedTeam: null,
          selectedAssociation: null,
          overrideColors: false,
          search: '',
          activeTab: personalTeams.length ? 'teams' : 'association'
        });
      }

      if (!hasFetched.current) {
        fetchStoreCreateOptions(null, triggerNotification);
        hasFetched.current = true;
      }
    }
  }, [isOpen, loading]);

  const onSelectTeam = (selectedTeam) => {
    setFormState({ ...formState, selectedTeam });
  };

  const onSelectAssociation = (selectedAssociation) => {
    setFormState({ ...formState, selectedAssociation });
  };

  return (
    <ResponsiveModal
      formSectionProps={
        formState.selectedAssociation
          ? {
              cancelTitle: 'Back',
              onCancel: () =>
                setFormState({
                  ...formState,
                  selectedAssociation: null,
                  overrideColors: false
                }),
              onSubmit: () => {
                onConfirm({
                  association: formState.selectedAssociation,
                  regAssociation: formState.overrideColors
                    ? selectedRegAssociation
                    : null
                });
              },
              submitTitle: 'Confirm association'
            }
          : formState.selectedTeam
          ? {
              cancelTitle: 'Back',
              onCancel: () =>
                setFormState({
                  ...formState,
                  selectedTeam: null,
                  overrideColors: false
                }),
              onSubmit: () => {
                onConfirm({
                  team: formState.selectedTeam,
                  regAssociation: formState.overrideColors
                    ? selectedRegAssociation
                    : null
                });
              },
              submitTitle: 'Confirm team'
            }
          : {
              onCancel: onClose,
              isSubmitHidden: true,
              leftSection:
                isAssociationSelect && association ? (
                  <Anchor
                    onClick={onRemove}
                    style={{ color: '#c40000', fontSize: 14 }}
                  >
                    Remove association
                  </Anchor>
                ) : (
                  team && (
                    <Anchor
                      onClick={onRemove}
                      style={{ color: '#c40000', fontSize: 14 }}
                    >
                      Remove team
                    </Anchor>
                  )
                )
            }
      }
      isLoading={loading}
      isOpen={isOpen}
      onClose={onClose}
      title={
        isAssociationSelect ? 'Select store association' : 'Select store team'
      }
    >
      {formState.selectedTeam || formState.selectedAssociation ? (
        <AppStack>
          {formState.selectedTeam && (
            <AppStack style={{ gap: 5 }}>
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>Team</AppText>
              <SettingSelector
                disabled
                leftSection={
                  <Image
                    fit="contain"
                    h="100%"
                    src={formState.selectedTeam.image}
                    w="100%"
                  />
                }
                rightSection={<></>}
                style={{ cursor: 'default' }}
                title={formState.selectedTeam.name}
                withBorder
              />
            </AppStack>
          )}

          <AppStack style={{ gap: 5 }}>
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              Association
            </AppText>
            <SettingSelector
              disabled
              leftSection={
                <Image
                  fit="contain"
                  h="100%"
                  src={
                    formState.selectedAssociation?.image ??
                    formState.selectedTeam.associationImage
                  }
                  w="100%"
                />
              }
              rightSection={<></>}
              style={{ cursor: 'default' }}
              title={
                formState.selectedAssociation?.name ??
                formState.selectedTeam.associationName
              }
              withBorder
            />
          </AppStack>

          <Divider />

          <Checkbox
            checked={formState.overrideColors}
            disabled={!selectedRegAssociation}
            error={
              !selectedRegAssociation
                ? 'No default branding available for this association.'
                : null
            }
            label="Override branding with association colors"
            onChange={() =>
              setFormState({
                ...formState,
                overrideColors: !formState.overrideColors
              })
            }
            style={{ fontWeight: 500, marginTop: 10 }}
          />
        </AppStack>
      ) : isAssociationSelect ? (
        <AppStack style={{ gap: 10 }}>
          <AppStack style={{ gap: 0 }}>
            <TextInput
              onChange={(e) =>
                setFormState({
                  ...formState,
                  search: e.currentTarget.value
                })
              }
              placeholder="Search associations by name"
              size="sm"
              style={{ marginTop: 10 }}
              value={formState.search}
            />
          </AppStack>
          <AppStack
            style={{
              flex: 1,
              border: 'solid 1px lightgrey',
              padding: 5,
              borderRadius: 5,
              height: '100%',
              overflow: 'hidden',
              gap: 3
            }}
          >
            {associationOptions.length === 0 ? (
              <AppStack
                style={{
                  padding: 20,
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 5
                }}
              >
                <AlertCircle color="#000" size={30} />
                <AppText style={{ fontSize: 16, color: '#000' }}>
                  No associations found
                </AppText>
              </AppStack>
            ) : (
              associationOptions
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((a) => (
                  <CustomNavLink
                    key={a.pkAssociation}
                    label={a.name}
                    leftSection={
                      <Avatar color="dark" radius="xl" size="md" src={a?.image}>
                        <AppText style={{ fontWeight: 500 }}>
                          {getInitialsFromName(a.name)}
                        </AppText>
                      </Avatar>
                    }
                    onClick={() => onSelectAssociation(a)}
                  />
                ))
            )}
          </AppStack>
        </AppStack>
      ) : (
        <AppStack
          style={{
            flex: 1,
            gap: 10,
            overflow: 'hidden',
            height: '100%'
          }}
        >
          <AppStack style={{ gap: 0 }}>
            <Tabs
              onChange={(activeTab) =>
                setFormState({ ...formState, activeTab })
              }
              value={formState.activeTab}
            >
              <Tabs.List>
                {loading ? (
                  <>
                    <Tabs.Tab color="dark" value="teams">
                      <Skeleton height={16} width={80} />
                    </Tabs.Tab>
                    <Tabs.Tab color="dark" value="association">
                      <Skeleton height={16} width={120} />
                    </Tabs.Tab>
                  </>
                ) : (
                  <>
                    <Tabs.Tab color="dark" value="teams">
                      <AppText
                        style={{
                          fontSize: 14,
                          fontWeight:
                            formState.activeTab === 'teams' ? 500 : 'normal'
                        }}
                      >
                        Your teams
                      </AppText>
                    </Tabs.Tab>
                    {regAssociationTeams.length > 0 && (
                      <Tabs.Tab color="dark" value="association">
                        <AppText
                          style={{
                            fontSize: 14,
                            fontWeight:
                              formState.activeTab === 'association'
                                ? 500
                                : 'normal'
                          }}
                        >
                          Association teams
                        </AppText>
                      </Tabs.Tab>
                    )}
                  </>
                )}
              </Tabs.List>
            </Tabs>

            <TextInput
              disabled={
                loading ||
                (personalTeams.length === 0 &&
                  formState.activeTab === 'teams') ||
                (regAssociationTeams.length === 0 &&
                  formState.activeTab === 'association')
              }
              onChange={(e) =>
                setFormState({
                  ...formState,
                  search: e.currentTarget.value
                })
              }
              placeholder="Search teams by name"
              size="sm"
              style={{ marginTop: 5 }}
              value={formState.search}
            />
          </AppStack>
          <AppStack
            style={{
              flex: 1,
              border: 'solid 1px lightgrey',
              padding: 5,
              borderRadius: 5,
              height: '100%',
              overflow: 'hidden'
            }}
          >
            {filteredTeams.length === 0 ? (
              <AppStack
                style={{
                  padding: 20,
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 5
                }}
              >
                <AlertCircle color="#000" size={30} />
                <AppText style={{ fontSize: 16, color: '#000' }}>
                  No teams found
                </AppText>

                {formState.search && (
                  <Button
                    color="dark"
                    onClick={() => {
                      setFormState({ ...formState, search: '' });
                    }}
                    variant="outline"
                  >
                    Clear search
                  </Button>
                )}
              </AppStack>
            ) : (
              filteredTeams
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((t) => (
                  <CustomNavLink
                    key={t.pkTeam}
                    description={t.associationName}
                    label={t.name}
                    leftSection={
                      <Avatar color="dark" radius="xl" size="md" src={t.image}>
                        <AppText style={{ fontWeight: 500 }}>
                          {getInitialsFromName(t.name)}
                        </AppText>
                      </Avatar>
                    }
                    onClick={() => onSelectTeam(t)}
                  />
                ))
            )}
          </AppStack>
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

SelectStoreTeamModal.propTypes = {
  association: PropTypes.object,
  isAssociationSelect: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  onRemove: PropTypes.func,
  team: PropTypes.object
};

export default SelectStoreTeamModal;
